import { useEffect } from "react";
import { pageDescriptions } from "@/data/pageDescriptions";
import { useLocation } from "react-router-dom";

/**
 * Removes duplicate head tags left over from the static index.html.
 *
 * react-helmet-async only manages the tags it inserts (marked data-rh="true").
 * The static tags in index.html stay in place, so any route that sets its own
 * <meta name="description"> or og:* tag ships TWO of them and crawlers may
 * pick either one. This strips the static copy whenever a Helmet-managed
 * equivalent exists, and leaves it alone as a fallback when it doesn't.
 */
const DEDUPE_SELECTORS = [
  'meta[name="description"]',
  'meta[property="og:title"]',
  'meta[property="og:description"]',
  'meta[property="og:url"]',
  'meta[property="og:type"]',
  'meta[name="twitter:title"]',
  'meta[name="twitter:description"]',
  'link[rel="canonical"]',
];

const dedupeHead = () => {
  const description = pageDescriptions[window.location.pathname.replace(/\/$/, "") || "/"];
  if (description) {
    for (const selector of ['meta[name="description"]', 'meta[property="og:description"]', 'meta[name="twitter:description"]']) {
      let tag = document.head.querySelector<HTMLMetaElement>(selector);
      if (!tag) {
        tag = document.createElement("meta");
        const match = selector.match(/\[(name|property)="([^"]+)"\]/);
        if (match) tag.setAttribute(match[1], match[2]);
        document.head.appendChild(tag);
      }
      tag.content = description;
    }
  }
  DEDUPE_SELECTORS.forEach((selector) => {
    const all = Array.from(document.head.querySelectorAll(selector));
    if (all.length < 2) return;

    const managed = all.filter((el) => el.getAttribute("data-rh") === "true");
    if (managed.length === 0) return;

    // Keep the last Helmet-managed tag; drop the static duplicates and any
    // stale Helmet leftovers.
    const keep = managed[managed.length - 1];
    all.forEach((el) => {
      if (el !== keep) el.remove();
    });
  });
};

const HeadDedupe = () => {
  const location = useLocation();

  useEffect(() => {
    // Helmet applies its tags asynchronously after render, so run once on the
    // next frame and again shortly after to catch late route-level Helmets.
    const observer = new MutationObserver(dedupeHead);
    observer.observe(document.head, { childList: true });
    const raf = requestAnimationFrame(dedupeHead);
    const t1 = window.setTimeout(dedupeHead, 150);
    const t2 = window.setTimeout(dedupeHead, 600);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [location.pathname]);

  return null;
};

export default HeadDedupe;
